import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

// Parse the JSON rows safely so a bad entry doesn't blank the drawer
const parseRows = (raw) => {
  try {
    const r = JSON.parse(raw || "[]");
    return Array.isArray(r) ? r : [];
  } catch { return []; }
};

export default function SizeGuideDrawer({ open, onClose, garmentMeasurements, lookName }) {
  const [s, setS] = useState(null);

  useEffect(() => {
    if (!open || s) return;
    api.get("/settings").then(({ data }) => setS(data)).catch(() => setS({}));
  }, [open, s]);

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);

  if (!open) return null;
  const settings = s || {};
  const rows = parseRows(settings.size_guide_rows_json);
  const hasGarment = (garmentMeasurements || "").trim().length > 0;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="size-guide-drawer">
      <div className="absolute inset-0 bg-black/25" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[460px] flex flex-col" style={{ background: "#FDFBF7" }}>
        <div className="flex items-center justify-between px-6 py-5 border-b" style={{ borderColor: "#EBE5ED" }}>
          <div>
            <div className="text-[10px] uppercase tracking-editorial text-[#C3B1C5]">◐ ONE LOOK</div>
            <div className="font-serif-display text-[20px]">Size Guide</div>
          </div>
          <button onClick={onClose} data-testid="size-guide-drawer-close" aria-label="Close"><X size={20} strokeWidth={1.2} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-7">
          {hasGarment && (
            <div className="border p-4" style={{ borderColor: "#EBE5ED", background: "#EBE5ED22" }} data-testid="drawer-garment-measurements">
              <div className="text-[10px] uppercase tracking-editorial text-[#C3B1C5] mb-1">Garment measurements</div>
              {lookName && <div className="font-serif-display text-[15px] mb-2">{lookName}</div>}
              <div className="text-[13px] leading-relaxed whitespace-pre-wrap text-[#4A464A]/85">{garmentMeasurements}</div>
            </div>
          )}

          <p className="text-[13px] leading-relaxed text-[#4A464A]/80">
            {settings.size_guide_intro || "Our sizes run in a relaxed Asian fit. Find your size using your body measurements below."}
          </p>

          <div className="border" style={{ borderColor: "#EBE5ED" }}>
            <div className="grid grid-cols-4 text-[10px] uppercase tracking-editorial px-3 py-2.5 border-b" style={{ borderColor: "#EBE5ED", background: "#EBE5ED55" }}>
              <div>Size</div><div>Bust</div><div>Waist</div><div>Hip</div>
            </div>
            {rows.map((r, idx) => r.bust_only ? (
              <div key={idx} className="grid grid-cols-4 text-[13px] px-3 py-3 border-b last:border-b-0" style={{ borderColor: "#EBE5ED" }}>
                <div className="font-serif-display text-[14px]">{r.size}</div>
                <div className="col-span-3 text-[#4A464A]/80">{r.bust}</div>
              </div>
            ) : (
              <div key={idx} className="grid grid-cols-4 text-[13px] px-3 py-3 border-b last:border-b-0" style={{ borderColor: "#EBE5ED" }}>
                <div className="font-serif-display text-[14px]">{r.size}</div>
                <div className="text-[#4A464A]/80">{r.bust}</div>
                <div className="text-[#4A464A]/80">{r.waist}</div>
                <div className="text-[#4A464A]/80">{r.hip}</div>
              </div>
            ))}
            <div className="text-[10px] uppercase tracking-editorial text-[#4A464A]/50 px-3 py-2" style={{ background: "#EBE5ED22" }}>Body measurements, cm</div>
          </div>

          <div>
            <div className="font-serif-display text-[18px] mb-3">How to measure</div>
            <ul className="space-y-2.5 text-[13px] leading-relaxed text-[#4A464A]/85">
              <li className="flex gap-3"><span className="text-[#C3B1C5]">◇</span><span>{settings.size_guide_how_bust || "Bust: around the fullest part of your chest, tape kept level."}</span></li>
              <li className="flex gap-3"><span className="text-[#C3B1C5]">◇</span><span>{settings.size_guide_how_waist || "Waist: around the narrowest part of your waist."}</span></li>
              <li className="flex gap-3"><span className="text-[#C3B1C5]">◇</span><span>{settings.size_guide_how_hip || "Hip: around the fullest part of your hips."}</span></li>
            </ul>
          </div>

          <ul className="space-y-2 text-[12px] italic text-[#4A464A]/70 leading-relaxed">
            {[settings.size_guide_note_1, settings.size_guide_note_2, settings.size_guide_note_3]
              .filter(n => (n || "").trim())
              .map((n, i) => <li key={i}>· {n}</li>)}
          </ul>
        </div>

        <div className="border-t px-6 py-4 text-center" style={{ borderColor: "#EBE5ED" }}>
          <Link to="/size-guide" onClick={onClose} data-testid="drawer-full-guide-link" className="text-[11px] uppercase tracking-editorial border-b pb-0.5" style={{ borderColor: "#4A464A" }}>
            View the full size guide →
          </Link>
        </div>
      </aside>
    </div>
  );
}
